import React, { useState, useCallback, useRef } from 'react';
import { CommonModal } from './index';

/**
 * useConfirmModal — A reusable confirmation modal hook.
 *
 * Usage:
 *   const { confirm, ConfirmModal } = useConfirmModal();
 *
 *   // Trigger confirmation (returns Promise<boolean>)
 *   const handleDelete = async () => {
 *       const confirmed = await confirm('Delete this item?', {
 *           title: 'Delete Item',
 *           confirmLabel: 'Delete',
 *           cancelLabel: 'Cancel',
 *       });
 *       if (!confirmed) return;
 *       // ...proceed
 *   };
 *
 *   // Render <ConfirmModal /> somewhere in your JSX
 *   return <>{...your UI}<ConfirmModal /></>
 */

const useConfirmModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [options, setOptions] = useState({});
    const resolveRef = useRef(null);

    const confirm = useCallback((msg, opts = {}) => {
        setMessage(msg);
        setOptions(opts);
        setIsOpen(true);
        return new Promise((resolve) => {
            resolveRef.current = resolve;
        });
    }, []);

    const handleConfirm = () => {
        setIsOpen(false);
        resolveRef.current && resolveRef.current(true);
    };

    const handleCancel = () => {
        setIsOpen(false);
        resolveRef.current && resolveRef.current(false);
    };

    const ConfirmModal = () => (
        <CommonModal
            isOpen={isOpen}
            title={options.title || 'Confirm'}
            toggler={handleCancel}
            size={options.size || 'md'}
            onSubmit={false}
            onDelete={handleConfirm}
            onDeleteLabel={options.confirmLabel || 'Confirm'}
            onCancelLabel={options.cancelLabel || 'Cancel'}
        >
            <p>{message}</p>
        </CommonModal>
    );

    return { confirm, ConfirmModal };
};

export default useConfirmModal;
